import { AnalyticsBrowser } from "@segment/analytics-next";
import {
  Dispatch,
  SetStateAction,
  FC,
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import Disposition from "../../types/Disposition";
import PatientDisposition from "../../types/PatientDisposition";
import DispositionSelector from "../disposition-selector";
import IntelligenceTextDisplay from "../intelligence-text";
import SuggestionConfirmation from "../suggestion-confirmation";
import { Box } from "@mui/material";
import { useMutation, useQueryClient } from "react-query";
import { updatePatientDispositions } from "../../api/endpoints";
import { ActionTrack } from "../../types/Analytics";
import IntelligenceText from "../../types/IntelligenceText";
import { useUpdatePatientCache } from "../../hooks/use-update-patient-cache";

interface DispositionSelectorInlineProps {
  selectableDispositions: Disposition[];
  currentUserEnteredPatientDispositions: PatientDisposition[];
  suggestedDisposition?: PatientDisposition;
  intelText?: IntelligenceText;
  patientID: number;
  isMultiDispoEnabled: boolean;
  analytics: AnalyticsBrowser;
  setShowErrorToast: Dispatch<SetStateAction<boolean>>;
  disabled?: boolean;
}

const DispositionSelectorInline: FC<DispositionSelectorInlineProps> = ({
  selectableDispositions,
  currentUserEnteredPatientDispositions,
  suggestedDisposition,
  intelText,
  patientID,
  isMultiDispoEnabled,
  analytics,
  setShowErrorToast,
  disabled,
}) => {
  // Memoize to prevent recreation on every render
  // Filter out dispositions where disposition is undefined or display_name is missing (Edge case)
  const userEnteredDispositions = useMemo(
    () =>
      currentUserEnteredPatientDispositions
        .map((pd) => pd.disposition)
        .filter((d) => d && d.display_name) as Disposition[],
    [currentUserEnteredPatientDispositions]
  );

  const [currentDispositions, setCurrentDispositions] = useState(
    userEnteredDispositions || []
  );
  const [suggestionActionTaken, setSuggestionActionTaken] = useState(false);

  // Create a stable key for comparison (IDs as string)
  const dispositionIdsKey = useMemo(
    () =>
      userEnteredDispositions
        .map((d) => d.id)
        .sort()
        .join(","),
    [userEnteredDispositions]
  );

  // Track the current suggestion to detect when a new suggestion arrives
  // Use ID if available, otherwise use a combination of disposition ID and timestamp
  const suggestionKey = useMemo(
    () =>
      suggestedDisposition
        ? `${suggestedDisposition.id ?? "no-id"}-${
            suggestedDisposition.disposition?.id ?? "no-dispo"
          }-${suggestedDisposition.qv_created_ts ?? ""}`
        : null,
    [suggestedDisposition]
  );
  const intelTextKey = useMemo(
    () => (intelText ? `${intelText.id}-${intelText.qv_created_ts}` : null),
    [intelText]
  );

  // Sync state when the actual dispositions change (but don't reset suggestionActionTaken here)
  useEffect(() => {
    setCurrentDispositions(userEnteredDispositions);
  }, [dispositionIdsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset suggestion action only when a new suggestion or intel text arrives
  // This allows showing new suggestions even after a previous action was taken
  useEffect(() => {
    setSuggestionActionTaken(false);
  }, [suggestionKey, intelTextKey]);

  const queryClient = useQueryClient();
  const { updateSuggestedDispositionStatus } = useUpdatePatientCache();

  const updatePatientDispositionMutation = useMutation(
    updatePatientDispositions,
    {
      onSuccess: (data) => {
        // Update the list cache directly with the returned data
        updateSuggestedDispositionStatus(patientID, data.id, data.status);
        queryClient.invalidateQueries({
          queryKey: ["patientdispositions", patientID],
        });
      },
      onError: () => {
        setCurrentDispositions(userEnteredDispositions);
        setSuggestionActionTaken(false);
        setShowErrorToast(true);
      },
    }
  );

  const handleSetCurrentDispositions = useCallback(
    (dispositions, isUserAction = true) => {
      setCurrentDispositions(dispositions);
      // Only set the flag if this is a user action, not when syncing from API or error reverts
      if (isUserAction) {
        setSuggestionActionTaken(true);
      }
    },
    []
  );

  const handleSuggestionAction = (status: string, suggestion: any) => {
    if (status === "ACCEPTED") {
      setCurrentDispositions([suggestion.disposition]);
    }
    setSuggestionActionTaken(true);

    updatePatientDispositionMutation.mutate({
      disposition: suggestion.disposition.id,
      status: status,
      id: suggestion.id,
    });

    analytics?.track(ActionTrack.SUGGESTION_ACTION, {
      type: "Disposition",
      suggestion_id: suggestion.id,
      suggestion_value: suggestion.disposition.display_name,
      status,
    });
  };

  let shouldDisplaySuggestion = suggestedDisposition !== null;

  if (suggestedDisposition && intelText) {
    shouldDisplaySuggestion =
      new Date(suggestedDisposition.qv_created_ts) >
      new Date(intelText.qv_created_ts);
  }

  const updatedDispositions = null;

  /** Suggestion / intel row only — popovers anchor below this so they do not cover the disposition control. */
  const inlineSuggestionAnchorRef = useRef<HTMLDivElement>(null);

  const finalCurrentDispositions = updatedDispositions
    ? updatedDispositions
    : currentDispositions;

  // only show the suggestion if we haven't taken an action
  // updatedDispositions is used for display purposes only, not to determine if suggestion should show
  const hasSuggestion =
    suggestedDisposition && !suggestionActionTaken && shouldDisplaySuggestion;

  const hasIntelText =
    intelText && !shouldDisplaySuggestion && !suggestionActionTaken;

  return (
    <Box sx={{ width: "100%", minWidth: 0, maxWidth: "100%" }}>
      <Box sx={{ minWidth: 0, maxWidth: "100%" }}>
        <DispositionSelector
          currentDispositions={finalCurrentDispositions}
          setCurrentDispositions={handleSetCurrentDispositions}
          selectableDispositions={selectableDispositions}
          currentUserEnteredDispositions={currentUserEnteredPatientDispositions}
          patientID={patientID}
          isInline
          isMultiDispoEnabled={isMultiDispoEnabled}
          analytics={analytics}
          setShowErrorToast={setShowErrorToast}
          hasSuggestion={hasSuggestion}
          disabled={disabled}
        />
      </Box>
      <Box ref={inlineSuggestionAnchorRef} sx={{ width: "100%", minWidth: 0 }}>
        {hasSuggestion ? (
          <SuggestionConfirmation
            suggestion={suggestedDisposition}
            suggestionType={"disposition"}
            updateFn={handleSuggestionAction}
            disabled={disabled}
            explainabilityAnchorRef={inlineSuggestionAnchorRef}
          />
        ) : hasIntelText ? (
          <IntelligenceTextDisplay
            intelligenceText={intelText}
            popoverAnchorRef={inlineSuggestionAnchorRef}
          />
        ) : (
          <></>
        )}
      </Box>
    </Box>
  );
};

export default DispositionSelectorInline;
